import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getLegalProfile, saveLegalProfile, requestEngagement, listScopes, listScopeMembers, apiError,
} from './engagementsApi';

const STEPS = ['Service', 'Legal entity', 'Contacts & environment', 'Scope', 'Providers & preferences', 'Review'];

const emptyContact = (role) => ({ role, name: '', phone: '', email: '' });
const emptyAsset = () => ({ asset: '', reference: '', env: 'Prod', stack: '' });
const emptyProvider = () => ({ name: '', services: '', approvalNeeded: 'N', reference: '' });

// Open items from the intake sheet; no defaults, the client decides.
const APPROACH = ['Black box (no information)', 'Grey box (user-level access)', 'White box (code + architecture)'];
const DEPTH = ['Confirm only', 'Exploit to demonstrate impact'];
const RECORDS = ['Not permitted', 'Permitted'];
const METHOD = ['OWASP Top 10; OWASP API Security Top 10', 'OWASP Top 10; OWASP API Security Top 10; OWASP ASVS', 'PTES', 'NIST SP 800-115'];
const SEVERITY = ['CVSS v3.1', 'CVSS v4.0'];
const AUDIENCE = ['Executive summary + technical detail', 'Executive summary only', 'Technical detail only'];
const ENV_OPTIONS = ['Production environment', 'Staging / pre-production environment', 'Dedicated test environment'];

/** Intake wizard; fields marked * are enforced here and on the server. */
export default function RequestWizard({ catalog, preselect, onClose, onCreated }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(preselect ? 1 : 0);
  const [serviceType, setServiceType] = useState(preselect || '');
  const [profile, setProfile] = useState({});
  const [intake, setIntake] = useState({
    contacts: [emptyContact('Client security / escalation contact'), emptyContact('Client technical contact')],
    assets: [emptyAsset()],
    providers: [emptyProvider()],
    environment: '',
    hasRealData: null,
    backupNote: '',
    preferredStartDate: '',
    testingWindows: 'No constraints',
    fragileComponents: '',
    testAccounts: 'Admin / User / Read-only, delivered via secure channel',
    wafChanges: 'None',
    outOfScope: '',
    sourceCodeRepo: '',
    apiSpecs: '',
    secureChannel: '',
    testingApproach: '',
    exploitationDepth: '',
    realRecordsAccess: '',
    methodology: METHOD[0],
    severityScheme: '',
    reportAudience: AUDIENCE[0],
    notes: '',
  });
  const [scopes, setScopes] = useState([]);
  const [scopeId, setScopeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getLegalProfile().then((p) => setProfile(p || {})).catch(() => {});
    listScopes().then(setScopes).catch(() => {});
  }, []);

  const set = (k, v) => setIntake((prev) => ({ ...prev, [k]: v }));
  const setRow = (k, i, field, v) => setIntake((prev) => {
    const rows = [...(prev[k] || [])];
    rows[i] = { ...rows[i], [field]: v };
    return { ...prev, [k]: rows };
  });
  const addRow = (k, make) => setIntake((prev) => ({ ...prev, [k]: [...(prev[k] || []), make()] }));
  const delRow = (k, i) => setIntake((prev) => ({ ...prev, [k]: prev[k].filter((_, j) => j !== i) }));

  const importScope = async (id) => {
    setScopeId(id);
    if (!id) return;
    try {
      const members = await listScopeMembers(id);
      const assets = members.map((m) => ({
        asset: m.label || m.identifier,
        reference: m.memberType === 'DOMAIN' ? `https://${m.identifier}/` : m.identifier,
        env: 'Prod',
        stack: m.memberType,
      }));
      if (assets.length) set('assets', assets);
    } catch (e) {
      setError(apiError(e, t('Engagements.scopeImportError', 'Could not import the scope members.')));
    }
  };

  const validateStep = () => {
    setError('');
    if (step === 0 && !serviceType) return t('Engagements.pickService', 'Pick a service to continue.');
    if (step === 1) {
      const need = ['legalName', 'entityType', 'registeredAddress', 'signatoryName', 'signatoryTitle'].filter((k) => !(profile[k] || '').trim());
      if (need.length) return t('Engagements.profileIncomplete', 'Legal name, entity type, registered address, signatory name and title are required.');
    }
    if (step === 2) {
      if (!intake.contacts.some((c) => c.name && c.email && c.phone)) {
        return t('Engagements.contactRequired', 'At least one contact needs a name, phone and email (Cataam must be able to reach you to pause testing).');
      }
      if (!intake.environment) return t('Engagements.envRequired', 'Describe the environment under test.');
      if (intake.hasRealData === null) return t('Engagements.realDataRequired', 'Tell us whether the environment holds real customer or personal data.');
      if (!intake.backupNote.trim()) return t('Engagements.backupRequired', 'Describe your backup regime; the agreements quote it.');
      if (!intake.fragileComponents.trim()) return t('Engagements.fragileRequired', 'List fragile components, or write "none".');
    }
    if (step === 3 && !intake.assets.some((a) => a.asset && a.reference)) {
      return t('Engagements.assetRequired', 'Add at least one in-scope asset with a URL or reference.');
    }
    if (step === 4) {
      const need = ['testingApproach', 'exploitationDepth', 'realRecordsAccess', 'severityScheme'].filter((k) => !intake[k]);
      if (need.length) return t('Engagements.choicesRequired', 'Choose the testing approach, exploitation depth, access to real records and severity scheme.');
    }
    return '';
  };

  const next = async () => {
    const msg = validateStep();
    if (msg) { setError(msg); return; }
    if (step === 1) {
      setBusy(true);
      try { await saveLegalProfile(profile); } catch (e) { setError(apiError(e, 'Could not save the legal profile.')); setBusy(false); return; }
      setBusy(false);
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      const clean = {
        ...intake,
        contacts: intake.contacts.filter((c) => c.name || c.email),
        assets: intake.assets.filter((a) => a.asset || a.reference),
        providers: intake.providers.filter((p) => p.name),
      };
      const e = await requestEngagement({ serviceType, intake: clean, scopeId: scopeId || null });
      onCreated(e);
    } catch (e) {
      setError(apiError(e, t('Engagements.requestError', 'Could not submit the request.')));
    } finally {
      setBusy(false);
    }
  };

  const svc = catalog.find((c) => c.serviceType === serviceType);
  const req = <span style={{ color: '#b91c1c' }}> *</span>;
  const field = (label, key, { type = 'text', help, required = false, full = false } = {}) => (
    <div className={`eng-field ${full ? 'full' : ''}`}>
      <label>{label}{required && req}</label>
      <input type={type} value={intake[key] || ''} onChange={(e) => set(key, e.target.value)} />
      {help && <div className="eng-help">{help}</div>}
    </div>
  );
  const select = (label, key, options, { required = false, placeholder } = {}) => (
    <div className="eng-field">
      <label>{label}{required && req}</label>
      <select value={intake[key] || ''} onChange={(e) => set(key, e.target.value)}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  const pf = (label, key, { required = false, full = false } = {}) => (
    <div className={`eng-field ${full ? 'full' : ''}`}>
      <label>{label}{required && req}</label>
      <input value={profile[key] || ''} onChange={(e) => setProfile({ ...profile, [key]: e.target.value })} />
    </div>
  );
  const choose = t('Engagements.choose', '— choose —');

  return (
    <div className="eng-overlay" onClick={onClose}>
      <div className="eng-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('Engagements.wizardTitle', 'Request a security service')}</h3>
        <div className="eng-sub">{t('Engagements.step', 'Step')} {step + 1} / {STEPS.length} · {STEPS[step]} · <span style={{ color: '#b91c1c' }}>*</span> {t('Engagements.required', 'required')}</div>
        <div className="eng-wiz-progress">{STEPS.map((s, i) => <span key={s} className={i <= step ? 'on' : ''} />)}</div>
        {error && <p className="eng-error">{error}</p>}

        {step === 0 && (
          <div className="eng-service-pick">
            {catalog.map((c) => (
              <div key={c.serviceType} className={`eng-service-opt ${serviceType === c.serviceType ? 'on' : ''}`} onClick={() => setServiceType(c.serviceType)}>
                <strong>{c.name}</strong>
                <small>{c.description}</small>
                <div style={{ marginTop: 6 }}>{(c.requiredDocuments || []).map((d) => <span key={d.templateCode} className="eng-chip">{d.templateCode}</span>)}</div>
              </div>
            ))}
          </div>
        )}

        {step === 1 && (
          <>
            <p className="eng-help">{t('Engagements.profileHelp', 'This is how your organisation is named in the agreements, e.g. "Acme Ltd (trading as Acme), a private limited company, having its address at …, registration no. …, acting through its director Jane Doe". The signatory is the person authorised to sign for the organisation.')}</p>
            <div className="eng-form">
              {pf(t('Engagements.legalName', 'Legal name'), 'legalName', { required: true })}
              {pf(t('Engagements.tradingName', 'Trading name (if different)'), 'tradingName')}
              {pf(t('Engagements.entityType', 'Entity type (e.g. private limited company, proprietorship, LLC)'), 'entityType', { required: true })}
              {pf(t('Engagements.registrationNumber', 'Registration number (CIN, Udyam, EIN, company no.)'), 'registrationNumber')}
              {pf(t('Engagements.registeredAddress', 'Registered address'), 'registeredAddress', { required: true, full: true })}
              {pf(t('Engagements.jurisdiction', 'Country / jurisdiction'), 'jurisdiction')}
              {pf(t('Engagements.signatoryName', 'Authorised signatory name'), 'signatoryName', { required: true })}
              {pf(t('Engagements.signatoryTitle', 'Signatory title'), 'signatoryTitle', { required: true })}
              {pf(t('Engagements.signatoryEmail', 'Signatory email'), 'signatoryEmail')}
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="eng-field full"><label>{t('Engagements.contacts', 'Contacts: security / escalation and technical')}{req}</label>
              <div className="eng-help">{t('Engagements.contactsHelp', 'Named in the Authorisation to Test. Cataam phones the escalation contact for critical findings and when asked to pause.')}</div></div>
            <table className="eng-rows">
              <tbody>
                {intake.contacts.map((c, i) => (
                  <tr key={i}>
                    <td style={{ width: '28%' }}><input value={c.role} onChange={(e) => setRow('contacts', i, 'role', e.target.value)} placeholder="Role" /></td>
                    <td><input value={c.name} onChange={(e) => setRow('contacts', i, 'name', e.target.value)} placeholder="Name *" /></td>
                    <td><input value={c.phone} onChange={(e) => setRow('contacts', i, 'phone', e.target.value)} placeholder="Phone / WhatsApp *" /></td>
                    <td><input value={c.email} onChange={(e) => setRow('contacts', i, 'email', e.target.value)} placeholder="Email *" /></td>
                    <td style={{ width: 34 }}><button className="eng-btn-ghost eng-btn-sm" onClick={() => delRow('contacts', i)}><i className="fas fa-times" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="eng-btn-ghost eng-btn-sm" onClick={() => addRow('contacts', () => emptyContact(''))}><i className="fas fa-plus" /> {t('Engagements.addContact', 'Add contact')}</button>
            <div className="eng-form" style={{ marginTop: 12 }}>
              {select(t('Engagements.environment', 'Environment under test'), 'environment', ENV_OPTIONS, { required: true, placeholder: choose })}
              <div className="eng-field">
                <label>{t('Engagements.hasRealData', 'Does it hold real customer or personal data?')}{req}</label>
                <select value={intake.hasRealData === null ? '' : intake.hasRealData ? 'yes' : 'no'} onChange={(e) => set('hasRealData', e.target.value === '' ? null : e.target.value === 'yes')}>
                  <option value="">{choose}</option><option value="no">No</option><option value="yes">Yes</option>
                </select>
                <div className="eng-help">{t('Engagements.realDataHelp', 'This is a declaration in the agreements. Tell Cataam immediately if it changes.')}</div>
              </div>
              {field(t('Engagements.backupNote', 'Backup regime (e.g. "backups are taken every 12 hours")'), 'backupNote', { required: true, full: true, help: t('Engagements.backupHelp', 'Quoted in the Liability Addendum and the Authorisation to Test.') })}
              {field(t('Engagements.preferredStartDate', 'Preferred start date'), 'preferredStartDate', { type: 'date' })}
              {field(t('Engagements.testingWindows', 'Testing windows / blackout times'), 'testingWindows')}
              <div className="eng-field full">
                <label>{t('Engagements.fragile', 'Fragile or shared components that could be disrupted')}{req}</label>
                <textarea value={intake.fragileComponents} onChange={(e) => set('fragileComponents', e.target.value)} placeholder={t('Engagements.fragilePh', 'List them, or write "none"')} />
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            {scopes.length > 0 && (
              <div className="eng-field" style={{ marginBottom: 10 }}>
                <label>{t('Engagements.importScope', 'Import assets from Scope & Boundary')}</label>
                <select value={scopeId} onChange={(e) => importScope(e.target.value)}>
                  <option value="">{t('Engagements.noImport', '— enter manually —')}</option>
                  {scopes.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            )}
            <div className="eng-field full"><label>{t('Engagements.assets', 'In-scope assets (Schedule 1)')}{req}</label></div>
            <table className="eng-rows">
              <thead><tr><th>Asset *</th><th>URL / reference *</th><th style={{ width: 90 }}>Env.</th><th>Stack</th><th /></tr></thead>
              <tbody>
                {intake.assets.map((a, i) => (
                  <tr key={i}>
                    <td><input value={a.asset} onChange={(e) => setRow('assets', i, 'asset', e.target.value)} placeholder="Customer console" /></td>
                    <td><input value={a.reference} onChange={(e) => setRow('assets', i, 'reference', e.target.value)} placeholder="https://console.example.com/" /></td>
                    <td><select value={a.env} onChange={(e) => setRow('assets', i, 'env', e.target.value)}><option>Prod</option><option>Staging</option><option>Dev</option><option>N/A</option></select></td>
                    <td><input value={a.stack} onChange={(e) => setRow('assets', i, 'stack', e.target.value)} placeholder="Next.js + API" /></td>
                    <td style={{ width: 34 }}><button className="eng-btn-ghost eng-btn-sm" onClick={() => delRow('assets', i)}><i className="fas fa-times" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="eng-btn-ghost eng-btn-sm" onClick={() => addRow('assets', emptyAsset)}><i className="fas fa-plus" /> {t('Engagements.addAsset', 'Add asset')}</button>
            <div className="eng-form" style={{ marginTop: 12 }}>
              {field(t('Engagements.outOfScope', 'Explicitly out of scope'), 'outOfScope')}
              {field(t('Engagements.sourceCodeRepo', 'Source-code repository to review (optional)'), 'sourceCodeRepo')}
              {field(t('Engagements.apiSpecs', 'API specifications (OpenAPI): where they are or how you will share them'), 'apiSpecs', { full: true, help: t('Engagements.apiSpecsHelp', 'The MSA requires you to provide them for API testing.') })}
            </div>
            <p className="eng-help" style={{ marginTop: 10 }}>{t('Engagements.authorityNote', 'By continuing you confirm that your organisation owns these assets or has the owner\'s permission to authorise testing. You will sign a written authorisation to that effect.')}</p>
          </>
        )}

        {step === 4 && (
          <>
            <div className="eng-field full"><label>{t('Engagements.providers', 'Hosting / edge providers (AWS, Cloudflare, …)')}</label>
              <div className="eng-help">{t('Engagements.providersHelp', 'Some providers require notice or approval for security testing; this feeds the Hosting Provider Acknowledgement.')}</div></div>
            <table className="eng-rows">
              <thead><tr><th>Provider</th><th>Services used</th><th style={{ width: 120 }}>Approval needed?</th><th>Reference / date</th><th /></tr></thead>
              <tbody>
                {intake.providers.map((p, i) => (
                  <tr key={i}>
                    <td><input value={p.name} onChange={(e) => setRow('providers', i, 'name', e.target.value)} placeholder="AWS" /></td>
                    <td><input value={p.services} onChange={(e) => setRow('providers', i, 'services', e.target.value)} placeholder="EC2, S3, API Gateway" /></td>
                    <td><select value={p.approvalNeeded} onChange={(e) => setRow('providers', i, 'approvalNeeded', e.target.value)}><option value="N">No</option><option value="Y">Yes</option></select></td>
                    <td><input value={p.reference} onChange={(e) => setRow('providers', i, 'reference', e.target.value)} /></td>
                    <td style={{ width: 34 }}><button className="eng-btn-ghost eng-btn-sm" onClick={() => delRow('providers', i)}><i className="fas fa-times" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="eng-btn-ghost eng-btn-sm" onClick={() => addRow('providers', emptyProvider)}><i className="fas fa-plus" /> {t('Engagements.addProvider', 'Add provider')}</button>
            <div className="eng-form" style={{ marginTop: 12 }}>
              {select(t('Engagements.testingApproach', 'Testing approach'), 'testingApproach', APPROACH, { required: true, placeholder: choose })}
              {select(t('Engagements.exploitationDepth', 'Exploitation depth'), 'exploitationDepth', DEPTH, { required: true, placeholder: choose })}
              {select(t('Engagements.realRecords', 'Access to real records as proof'), 'realRecordsAccess', RECORDS, { required: true, placeholder: choose })}
              {select(t('Engagements.severity', 'Severity scheme'), 'severityScheme', SEVERITY, { required: true, placeholder: choose })}
              {select(t('Engagements.methodology', 'Methodology (OWASP Top 10 + API Top 10 is the minimum)'), 'methodology', METHOD)}
              {select(t('Engagements.reportAudience', 'Report audience'), 'reportAudience', AUDIENCE)}
              {field(t('Engagements.testAccounts', 'Test accounts you will provide (per role)'), 'testAccounts')}
              {field(t('Engagements.wafChanges', 'WAF / rate-limit changes for testing'), 'wafChanges', { help: t('Engagements.wafHelp', 'Cataam provides its source IPs; you allow-list them or we test through the WAF.') })}
              {field(t('Engagements.secureChannel', 'Preferred secure channel for credentials'), 'secureChannel', { full: true })}
              <div className="eng-field full">
                <label>{t('Engagements.notes', 'Anything else Cataam should know')}</label>
                <textarea value={intake.notes} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>
          </>
        )}

        {step === 5 && (
          <div className="eng-review">
            <h4>{svc ? svc.name : serviceType}</h4>
            <div className="eng-help">{t('Engagements.reviewDocs', 'You will be asked to sign:')} {(svc?.requiredDocuments || []).map((d) => d.name).join(', ')}</div>
            <h4>{t('Engagements.legalEntity', 'Legal entity')}</h4>
            <div>{profile.legalName}{profile.tradingName ? ` (trading as ${profile.tradingName})` : ''}, {profile.entityType} · {profile.registeredAddress}</div>
            <div className="eng-help">{t('Engagements.signatory', 'Signatory')}: {profile.signatoryName}, {profile.signatoryTitle}</div>
            <h4>{t('Engagements.contactsShort', 'Contacts')}</h4>
            {intake.contacts.filter((c) => c.name).map((c, i) => <div key={i}>{c.role}: {c.name} · {c.phone} · {c.email}</div>)}
            <h4>{t('Engagements.environmentShort', 'Environment')}</h4>
            <div>{intake.environment} · {intake.hasRealData ? t('Engagements.holdsRealData', 'holds real data') : t('Engagements.noRealData', 'no real customer or personal data')} · {intake.backupNote}</div>
            <h4>{t('Engagements.scope', 'Scope')} ({intake.assets.filter((a) => a.asset).length})</h4>
            {intake.assets.filter((a) => a.asset).map((a, i) => <div key={i}>{a.asset} — {a.reference} ({a.env})</div>)}
            <h4>{t('Engagements.preferences', 'Testing preferences')}</h4>
            <div>{intake.testingApproach} · {intake.exploitationDepth} · {intake.realRecordsAccess} · {intake.methodology} · {intake.severityScheme}</div>
            <p className="eng-help" style={{ marginTop: 12 }}>
              {t('Engagements.reviewNote', 'After you submit, Cataam reviews the intake, fills in the commercial terms and issues the agreements for signature. You will be emailed when they are ready.')}
            </p>
          </div>
        )}

        <div className="eng-modal-foot">
          <button className="eng-btn" onClick={onClose}>{t('common.cancel', 'Cancel')}</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 0 && <button className="eng-btn" onClick={() => setStep(step - 1)} disabled={busy}>{t('common.back', 'Back')}</button>}
            {step < STEPS.length - 1
              ? <button className="eng-btn-primary" onClick={next} disabled={busy}>{t('common.next', 'Next')} <i className="fas fa-arrow-right" /></button>
              : <button className="eng-btn-primary" onClick={submit} disabled={busy}>{busy ? t('common.submitting', 'Submitting…') : t('Engagements.submit', 'Submit request')}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
